/**
 * DateInlineEditor — tap-to-edit date cell.
 *
 * Used on list views (tasks, plantings, animals.next_vet, vehicles.next_service).
 * Optimistic UI: the local value updates the moment the picker closes,
 * the PATCH fires in parallel. On rejection the row reverts.
 *
 * If `cascadePayload` is supplied, the editor intercepts the change
 * and renders the cascade preview modal first instead of going
 * straight to PATCH. The host's own onChange callback fires only
 * after the cascade is applied (via onCascadeApplied).
 */
import React, { useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CascadePreviewModal, CascadeChangePayload, CascadeApplyResult } from "@/components/CascadePreviewModal";

interface Props {
  value: string | null;
  /**
   * Called with the chosen ISO date when no cascade is involved.
   * Should perform the PATCH (or throw on failure).
   */
  onChange: (newValue: string) => Promise<void>;
  /**
   * Optional: when provided, the editor wraps the change in a
   * cascade preview before applying. The change object on the
   * payload should already be filled with the relevant field.
   * The editor injects the new date into change[fieldName] before
   * preview.
   */
  cascade?: {
    payloadBuilder: (newDate: string) => CascadeChangePayload;
    onApplied?: (result: CascadeApplyResult) => void;
  };
  /** Optional placeholder/short label when value is null */
  placeholder?: string;
  /** Disable editing — render value but don't open picker */
  readonly?: boolean;
  className?: string;
}

export function DateInlineEditor({
  value, onChange, cascade, placeholder = "Set date", readonly, className,
}: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string>(() => (value ?? "").split("T")[0]);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<"success" | "error" | null>(null);
  const [cascadeOpen, setCascadeOpen] = useState(false);
  const [cascadePayload, setCascadePayload] = useState<CascadeChangePayload | null>(null);

  const display = value
    ? new Date(value).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      })
    : placeholder;

  const handleSave = async () => {
    if (!draft) return;
    const iso = new Date(draft).toISOString();

    // Cascade flow: short-circuit and open preview modal
    if (cascade) {
      setCascadePayload(cascade.payloadBuilder(iso));
      setCascadeOpen(true);
      setOpen(false);
      return;
    }

    // Direct PATCH flow with optimistic UI
    setBusy(true);
    try {
      await onChange(iso);
      setFlash("success");
      setTimeout(() => setFlash(null), 300);
      setOpen(false);
    } catch (err) {
      console.error("[DateInlineEditor]", err);
      setFlash("error");
      setTimeout(() => setFlash(null), 300);
      // Revert local draft to server value
      setDraft((value ?? "").split("T")[0]);
    } finally {
      setBusy(false);
    }
  };

  if (readonly) {
    return <span className={className}>{display}</span>;
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`inline-flex items-center gap-1.5 text-sm rounded px-2 py-1 transition-colors ${
              flash === "success" ? "bg-moss/15 dark:bg-moss-dark/20" :
              flash === "error" ? "bg-clay/15 dark:bg-clay-dark/20" :
              "hover:bg-surface-raised dark:bg-surface-raised-dark"
            } ${value ? "" : "text-muted-foreground italic"} ${className ?? ""}`}
            aria-label={`Edit date: currently ${display}`}
          >
            <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
            {display}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" align="start">
          <div className="space-y-2">
            <Input
              type="date"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSave();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setOpen(false);
                }
              }}
            />
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => setOpen(false)} disabled={busy}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={busy || !draft}>
                {busy ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {cascade && cascadePayload && (
        <CascadePreviewModal
          open={cascadeOpen}
          payload={cascadePayload}
          onClose={() => {
            setCascadeOpen(false);
            setCascadePayload(null);
          }}
          onApplied={(result) => {
            // Cascade has already mutated the server. Fire the host
            // callback so it can refresh data + show Undo toast.
            cascade.onApplied?.(result);
            setFlash("success");
            setTimeout(() => setFlash(null), 300);
          }}
        />
      )}
    </>
  );
}
